import { useEffect } from "react";

/**
 * Lightweight per-route SEO manager.
 * Sets <title>, meta description, canonical, Open Graph and Twitter tags.
 */
export default function useSeo({
    title,
    description,
    canonicalPath = "/",
    ogImage = "/og-default.jpg",
    keywords,
    jsonLd,
} = {}) {
    useEffect(() => {
        const fullTitle = title
            ? `${title} | Vendagain`
            : "Vendagain — Perth's Custom Phone Case & Vending Solutions";
        document.title = fullTitle;

        const setMeta = (name, content, attr = "name") => {
            if (!content) return;
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };

        setMeta("description", description);
        if (keywords) setMeta("keywords", keywords);

        // Absolute image URL — social scrapers (WhatsApp, LinkedIn, iMessage)
        // reject relative paths.
        const base = window.location.origin;
        const absoluteOg = ogImage.startsWith("http") ? ogImage : `${base}${ogImage}`;

        setMeta("og:title", fullTitle, "property");
        setMeta("og:description", description, "property");
        setMeta("og:type", "website", "property");
        setMeta("og:image", absoluteOg, "property");
        setMeta("og:image:width", "1200", "property");
        setMeta("og:image:height", "630", "property");
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", fullTitle);
        setMeta("twitter:description", description);
        setMeta("twitter:image", absoluteOg);

        // Canonical
        const canonicalHref = `${base}${canonicalPath}`;
        let link = document.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement("link");
            link.rel = "canonical";
            document.head.appendChild(link);
        }
        link.href = canonicalHref;
        setMeta("og:url", canonicalHref, "property");

        // JSON-LD structured data
        // Remove any previous JSON-LD injected by this hook
        document
            .querySelectorAll('script[data-seo-jsonld="true"]')
            .forEach((s) => s.remove());
        if (jsonLd) {
            const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
            arr.forEach((obj) => {
                const s = document.createElement("script");
                s.type = "application/ld+json";
                s.dataset.seoJsonld = "true";
                s.text = JSON.stringify(obj);
                document.head.appendChild(s);
            });
        }
    }, [title, description, canonicalPath, ogImage, keywords, jsonLd]);
}
